import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shareDailyReport, todayISO } from "@/lib/pgt";
import { toast } from "sonner";
import CustomerCard from "@/components/CustomerCard";
import DailyReportDialog from "@/components/DailyReportDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, CalendarClock, AlertTriangle, UserPlus, PhoneOff, FlaskConical, ThumbsUp, Receipt, RefreshCw, Moon, CheckCircle2, Ban, Users, Activity, Target, Send, ClipboardCheck, Share2, Hourglass, TrendingUp, TrendingDown } from "lucide-react";

const CATS = [
  { key: "due_today", label: "Due Today", icon: CalendarClock, tone: "text-forest", bg: "bg-forest-light" },
  { key: "overdue", label: "Overdue", icon: AlertTriangle, tone: "text-red-600", bg: "bg-red-50" },
  { key: "not_responding", label: "Not Responding", icon: PhoneOff, tone: "text-amber-600", bg: "bg-amber-50" },
  { key: "new_prospects", label: "New Prospects", icon: UserPlus, tone: "text-blue-600", bg: "bg-blue-50" },
  { key: "samples_feedback_pending", label: "Feedback Pending", icon: FlaskConical, tone: "text-purple-600", bg: "bg-purple-50" },
  { key: "positive_not_converted", label: "Positive · Not Converted", icon: ThumbsUp, tone: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "commercial_pending", label: "Commercial Pending", icon: Receipt, tone: "text-gold", bg: "bg-gold-bg" },
  { key: "repeat_due", label: "Repeat Purchase Due", icon: RefreshCw, tone: "text-cyan-700", bg: "bg-cyan-50" },
  { key: "excessive_followups", label: "Excessive Follow-ups", icon: Ban, tone: "text-red-600", bg: "bg-red-50" },
  { key: "orders_confirmed", label: "Orders Confirmed", icon: CheckCircle2, tone: "text-emerald-700", bg: "bg-emerald-50" },
  { key: "dormant", label: "Dormant / Archived", icon: Moon, tone: "text-stone-600", bg: "bg-stone-100" },
];

const NOTE = {
  overdue: "Follow-up overdue",
  due_today: "Follow-up due today",
  not_responding: "Awaiting a reply",
  excessive_followups: "Consider close-the-loop or archive",
  positive_not_converted: "Move toward an order",
  samples_feedback_pending: "Request feedback",
  repeat_due: "Repeat purchase due",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [active, setActive] = useState("due_today");
  const canFilter = user.role === "admin" || user.role === "manager";
  const [viewUser, setViewUser] = useState("all");

  const { data: team = [] } = useQuery({
    queryKey: ["team-users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: canFilter,
  });
  const salespeople = team.filter((u) => u.role === "executive" || u.role === "manager");

  const { data: teamSummary = [] } = useQuery({
    queryKey: ["team-summary"],
    queryFn: async () => (await api.get("/dashboard/team-summary")).data,
    enabled: canFilter,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", canFilter ? viewUser : "self"],
    queryFn: async () => {
      const params = canFilter && viewUser !== "all" ? { salesperson_id: viewUser } : {};
      return (await api.get("/dashboard", { params })).data;
    },
  });

  const counts = data?.counts || {};
  const categories = data?.categories || {};
  const items = categories[active] || [];
  const activeCat = CATS.find((c) => c.key === active);
  const viewedName = viewUser === "all" ? null : salespeople.find((u) => u.id === viewUser)?.name;

  return (
    <div className="animate-fade-up">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">
            {greeting()}, {user.name.split(" ")[0]}
          </h1>
          <p className="mt-1 text-sm text-stone-500">
            {viewedName
              ? `Viewing ${viewedName}'s follow-up queue and pipeline.`
              : "Your follow-up queue and pipeline at a glance."}{" "}
            {counts.completed_today || 0} follow-ups completed today.
          </p>
        </div>
        {canFilter && (
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-stone-400" />
            <Select value={viewUser} onValueChange={setViewUser}>
              <SelectTrigger className="w-[210px]" data-testid="dashboard-user-filter">
                <SelectValue placeholder="View by salesperson" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="dashboard-user-all">All team (everyone)</SelectItem>
                {salespeople.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`dashboard-user-${u.id}`}>
                    {u.name} · {u.role === "manager" ? "Manager" : "Executive"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <MyTargets user={user} />

      {(user.role === "executive" || user.role === "manager") && <DailyReportCta />}

      <IdleTrend scopedId={canFilter && viewUser !== "all" ? viewUser : null} />

      <MyWork user={user} />

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>
      ) : (
        <>
          {/* Team compare row */}
          {canFilter && teamSummary.length > 0 && (
            <div className="mb-5" data-testid="team-compare-row">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-stone-400">
                <Users className="h-3.5 w-3.5" /> Compare salespeople
              </div>
              <div className="flex gap-2.5 overflow-x-auto pb-1">
                <button
                  onClick={() => setViewUser("all")}
                  data-testid="team-compare-all"
                  className={`shrink-0 rounded-xl border px-3.5 py-2.5 text-left transition-all ${
                    viewUser === "all" ? "border-forest ring-1 ring-forest bg-forest-light" : "border-stone-200 bg-white hover:border-forest/40"
                  }`}
                >
                  <div className="text-xs font-semibold text-stone-800">All team</div>
                  <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                    <span className="font-bold text-red-600">{teamSummary.reduce((s, p) => s + p.overdue, 0)}</span>
                    <span className="text-stone-400">overdue</span>
                    <span className="font-bold text-forest">{teamSummary.reduce((s, p) => s + p.due_today, 0)}</span>
                    <span className="text-stone-400">due</span>
                  </div>
                </button>
                {teamSummary.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setViewUser(p.id)}
                    data-testid={`team-compare-${p.id}`}
                    className={`shrink-0 rounded-xl border px-3.5 py-2.5 text-left transition-all ${
                      viewUser === p.id ? "border-forest ring-1 ring-forest bg-forest-light" : "border-stone-200 bg-white hover:border-forest/40"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-stone-800">
                      {p.name.split(" ")[0]}
                      <span className="text-[9px] font-normal uppercase text-stone-400">{p.role === "manager" ? "Mgr" : "Exec"}</span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                      <span className={`font-bold ${p.overdue ? "text-red-600" : "text-stone-300"}`}>{p.overdue}</span>
                      <span className="text-stone-400">overdue</span>
                      <span className={`font-bold ${p.due_today ? "text-forest" : "text-stone-300"}`}>{p.due_today}</span>
                      <span className="text-stone-400">due</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Stat grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CATS.map((cat) => {
              const n = counts[cat.key] || 0;
              const isActive = active === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => setActive(cat.key)}
                  data-testid={`dashboard-tab-${cat.key.replace(/_/g, "-")}`}
                  className={`rounded-xl border p-3 text-left transition-all ${
                    isActive ? "border-forest ring-1 ring-forest bg-white" : "border-stone-200 bg-white hover:border-forest/40"
                  }`}
                >
                  <div className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${cat.bg} ${cat.tone}`}>
                    <cat.icon className="h-4 w-4" />
                  </div>
                  <div className="mt-2 font-heading text-2xl font-bold text-stone-900">{n}</div>
                  <div className="text-[11px] font-medium text-stone-500 leading-tight">{cat.label}</div>
                </button>
              );
            })}
          </div>

          {/* Active category list */}
          <div className="mt-8">
            <div className="mb-3 flex items-center gap-2">
              {activeCat && <activeCat.icon className={`h-5 w-5 ${activeCat.tone}`} />}
              <h2 className="font-heading text-lg font-semibold text-stone-900">{activeCat?.label}</h2>
              <span className="text-sm text-stone-400">({items.length})</span>
            </div>
            {items.length === 0 ? (
              <div className="rounded-xl border border-dashed border-stone-300 bg-white/50 py-14 text-center text-sm text-stone-400" data-testid="dashboard-empty">
                Nothing here right now. 
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {items.map((it, i) => {
                  const customer = it.customer || it;
                  const note = it.reason || NOTE[active];
                  return <CustomerCard key={customer.id + i} customer={customer} note={note} />;
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DailyReportCta() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const { data } = useQuery({
    queryKey: ["daily-report-status"],
    queryFn: async () => (await api.get("/daily-reports/me")).data,
  });
  const submitted = data?.submitted;
  const share = async () => {
    setSharing(true);
    try {
      const r = await shareDailyReport(todayISO(), user?.name);
      if (r === "cancelled") return;
      if (r === "downloaded") toast("Report PDF saved — attach it in WhatsApp");
      else if (r === "shared") toast.success("Ready to share report");
    } catch (err) { toast.error("Could not prepare the report PDF"); }
    finally { setSharing(false); }
  };
  return (
    <div className="mb-5 flex flex-col items-start justify-between gap-3 rounded-xl border border-stone-200 bg-white p-4 sm:flex-row sm:items-center" data-testid="daily-report-cta">
      <div className="flex items-center gap-2.5">
        <span className={`grid h-9 w-9 place-items-center rounded-full ${submitted ? "bg-emerald-100 text-emerald-600" : "bg-gold/15 text-gold"}`}>
          <ClipboardCheck className="h-4 w-4" />
        </span>
        <div>
          <div className="font-heading text-sm font-semibold text-stone-900">Daily work report</div>
          <div className="text-xs text-stone-500">{submitted ? "Submitted for today — share it or update it." : "Submit your end-of-day progress report."}</div>
        </div>
      </div>
      <div className="flex w-full items-center gap-2 sm:w-auto">
        {submitted && (
          <Button onClick={share} disabled={sharing} variant="outline" className="border-whatsapp/40 text-whatsapp hover:bg-whatsapp/10" data-testid="share-daily-report">
            {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Share2 className="mr-1.5 h-4 w-4" /> Share on WhatsApp</>}
          </Button>
        )}
        <Button onClick={() => setOpen(true)} className={submitted ? "" : "bg-forest hover:bg-forest-600 text-ivory"} variant={submitted ? "outline" : "default"} data-testid="open-daily-report">
          {submitted ? "View / update report" : "Submit daily report"}
        </Button>
      </div>
      <DailyReportDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function IdleTrend({ scopedId }) {
  const { data } = useQuery({
    queryKey: ["idle-trend", scopedId || "self"],
    queryFn: async () => (await api.get("/reports/idle-trend", { params: scopedId ? { salesperson_id: scopedId } : {} })).data,
  });
  if (!data) return null;
  const { currently_idle, this_week, last_week, threshold_days } = data;
  const delta = this_week - last_week;
  const trendText = delta === 0 ? "Same as last week" : delta > 0 ? `${delta} more than last week` : `${Math.abs(delta)} fewer than last week`;
  const TrendIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Activity;
  const trendTone = delta > 0 ? "text-red-500" : delta < 0 ? "text-emerald-600" : "text-stone-400";
  const cell = (label, value, tone) => (
    <div className="rounded-lg bg-stone-50 p-2.5" data-testid={`idle-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-[10px] text-stone-500">{label}</div>
      <div className={`font-heading text-xl font-bold ${tone || "text-stone-900"}`}>{value}</div>
    </div>
  );
  return (
    <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="idle-trend">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-heading text-sm font-semibold text-stone-900">
          <Hourglass className="h-4 w-4 text-amber-500" /> Idle customers
          <span className="text-[11px] font-normal text-stone-400">no contact {threshold_days}d+</span>
        </div>
        <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${trendTone}`} data-testid="idle-trend-delta">
          <TrendIcon className="h-3.5 w-3.5" /> {trendText}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {cell("Currently idle", currently_idle, "text-amber-600")}
        {cell("Went idle this week", this_week)}
        {cell("Last week", last_week)}
      </div>
    </div>
  );
}

function MyTargets({ user }) {
  const { data: goal } = useQuery({
    queryKey: ["my-goal"],
    queryFn: async () => (await api.get("/goals/me")).data,
  });
  const { data: task } = useQuery({
    queryKey: ["my-daily-task"],
    queryFn: async () => (await api.get("/daily-tasks/me")).data,
  });
  const hasGoal = goal?.has_goal;
  const hasTask = task?.has_task;
  if (!hasGoal && !hasTask) return null;

  const bar = (done, target, color) => {
    const pct = target > 0 ? Math.min(100, Math.round((done / target) * 100)) : 0;
    return (
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    );
  };

  return (
    <div className="mb-5 rounded-xl border border-forest/20 bg-forest-light/30 p-4" data-testid="my-targets">
      <div className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold text-forest">
        <Target className="h-4 w-4" /> Your targets{goal ? <span className="text-[11px] font-normal text-stone-500">· week {goal.week_start} → {goal.week_end}</span> : null}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {hasTask && (
          <div className="rounded-lg bg-white p-3" data-testid="target-daily">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 text-stone-500"><Send className="h-3.5 w-3.5" /> Today · messages</span>
              <span className="font-semibold text-stone-800" data-testid="target-daily-value">{task.messages_sent} / {task.message_target}</span>
            </div>
            {bar(task.messages_sent, task.message_target, "bg-forest")}
            {task.note && <p className="mt-1.5 text-[11px] text-stone-500">{task.note}</p>}
          </div>
        )}
        {hasGoal && (
          <div className="rounded-lg bg-white p-3" data-testid="target-conversions">
            <div className="flex items-center justify-between text-xs">
              <span className="text-stone-500">This week · conversions</span>
              <span className="font-semibold text-stone-800">{goal.conversions_done} / {goal.conversions_target || "—"}</span>
            </div>
            {bar(goal.conversions_done, goal.conversions_target, "bg-forest")}
          </div>
        )}
        {hasGoal && (
          <div className="rounded-lg bg-white p-3" data-testid="target-revenue">
            <div className="flex items-center justify-between text-xs">
              <span className="text-stone-500">This week · revenue</span>
              <span className="font-semibold text-stone-800">₹{Number(goal.revenue_done || 0).toLocaleString("en-IN")} / {goal.revenue_target ? "₹" + Number(goal.revenue_target).toLocaleString("en-IN") : "—"}</span>
            </div>
            {bar(goal.revenue_done, goal.revenue_target, "bg-gold")}
          </div>
        )}
      </div>
    </div>
  );
}

function MyWork({ user }) {
  const [p, setP] = useState("weekly");
  const { data } = useQuery({
    queryKey: ["my-work", user.id, p],
    queryFn: async () => (await api.get("/reports/activity", { params: { period: p, salesperson_id: user.id } })).data,
  });
  const t = data?.totals || {};
  const items = [
    ["Messages", t.messages_sent],
    ["Responses", t.responses],
    ["Follow-ups", t.followups_completed],
    ["Stage changes", t.stage_changes],
    ["Notes", t.notes],
  ];
  return (
    <div className="mb-5 rounded-xl border border-stone-200 bg-white p-4" data-testid="my-work-widget">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-heading text-sm font-semibold text-stone-900">
          <Activity className="h-4 w-4 text-forest" /> My work
        </div>
        <div className="flex rounded-lg border border-stone-200 p-0.5">
          {["daily", "weekly", "monthly"].map((x) => (
            <button
              key={x}
              onClick={() => setP(x)}
              data-testid={`my-work-${x}`}
              className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors ${p === x ? "bg-forest text-ivory" : "text-stone-500 hover:text-forest"}`}
            >
              {x}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        <div className="rounded-lg bg-forest-light/50 p-2.5">
          <div className="text-[10px] text-stone-500">Total</div>
          <div className="font-heading text-xl font-bold text-forest" data-testid="my-work-total">{t.total || 0}</div>
        </div>
        {items.map(([label, v]) => (
          <div key={label} className="rounded-lg bg-stone-50 p-2.5">
            <div className="text-[10px] text-stone-500">{label}</div>
            <div className="font-heading text-xl font-bold text-stone-900">{v || 0}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}
